import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, Trash2 } from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";

const TONE = {
  published: "border-emerald-200 bg-emerald-50 text-emerald-800",
  validated: "border-blue-200 bg-blue-50 text-blue-800",
  needs_review: "border-amber-200 bg-amber-50 text-amber-800",
  blocked: "border-rose-200 bg-rose-50 text-rose-800",
  draft: "border-slate-200 bg-slate-50 text-slate-700",
  superseded: "border-slate-200 bg-slate-100 text-slate-500",
};

const GroupRows = ({ g, expanded, onToggle, onAct, onDownloadOriginal }) => (
  <>
    <tr data-testid={`upload-group-${g.key}`}>
      <td>
        {g.batches.length > 1 ? (
          <button
            data-testid={`toggle-${g.key}`}
            onClick={onToggle}
            className="text-slate-500 hover:text-slate-900"
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : null}
      </td>
      <td className="max-w-[220px] truncate">{g.head.filename}</td>
      <td className="font-mono-num text-xs">
        {g.head.counselling_body} · {g.head.state} · {g.head.year}
      </td>
      <td>{g.head.data_type}</td>
      <td>
        <div className="flex flex-wrap gap-1">
          {g.batches.map((b) => (
            <span
              key={b.id}
              className={`border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${TONE[b.status]}`}
            >
              {b.round}
            </span>
          ))}
        </div>
      </td>
      <td className="text-right font-mono-num">{g.total_rows}</td>
      <td className="text-right font-mono-num">{g.total_errors}</td>
      <td className="font-mono-num text-xs">{new Date(g.latest_at).toLocaleString("en-IN")}</td>
      <td>
        {g.upload_id ? (
          <button
            data-testid={`download-original-${g.head.id}`}
            onClick={() => onDownloadOriginal(g.head.id, g.head.filename)}
            className="flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-[#1A365D]"
          >
            <Download className="h-3 w-3" /> Original
          </button>
        ) : (
          <span
            data-testid={`no-original-${g.head.id}`}
            title="Original file not retained"
            className="text-[11px] font-semibold text-slate-300"
          >
            Original n/a
          </span>
        )}
      </td>
    </tr>
    {(g.batches.length === 1 || expanded) &&
      g.batches.map((b) => (
        <tr key={b.id} className="bg-slate-50/60" data-testid={`batch-row-${b.id}`}>
          <td />
          <td className="pl-6 text-xs text-slate-500" colSpan={2}>
            Round <strong className="text-slate-900">{b.round}</strong>
          </td>
          <td />
          <td>
            <span className={`border px-1.5 py-0.5 text-[11px] font-semibold uppercase ${TONE[b.status]}`}>
              {b.status}
            </span>
          </td>
          <td className="text-right font-mono-num">{b.row_count}</td>
          <td className="text-right font-mono-num">{b.error_count}</td>
          <td className="font-mono-num text-xs">{new Date(b.created_at).toLocaleString("en-IN")}</td>
          <td>
            <div className="flex items-center gap-2">
              {b.status === "needs_review" && (
                <a
                  data-testid={`review-${b.id}`}
                  href={`/admin/batches/${b.id}/review`}
                  className="text-[11px] font-semibold text-amber-700 hover:underline"
                >
                  Review
                </a>
              )}
              {b.status === "blocked" && (
                <button
                  data-testid={`revalidate-${b.id}`}
                  onClick={() => onAct(b.id, "revalidate")}
                  className="text-[11px] font-semibold text-slate-600 hover:text-[#1A365D]"
                >
                  Re-validate
                </button>
              )}
              {b.status === "validated" && (
                <button
                  data-testid={`publish-${b.id}`}
                  onClick={() => onAct(b.id, "publish")}
                  className="text-[11px] font-semibold text-emerald-700 hover:underline"
                >
                  Publish
                </button>
              )}
              <button
                data-testid={`delete-batch-${b.id}`}
                onClick={() => onAct(b.id, "delete")}
                className="text-slate-400 hover:text-rose-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </td>
        </tr>
      ))}
  </>
);

export default function AdminBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState([]);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    try {
      const [b, l] = await Promise.all([api.get("/admin/uploads"), api.get("/admin/audit-log")]);
      setBatches(b.data);
      setLog(l.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Group batches by parent upload_id (one physical file -> N round batches).
  const groups = useMemo(() => {
    const by = new Map();
    for (const b of batches) {
      const key = b.upload_id || `single:${b.id}`;
      if (!by.has(key)) by.set(key, []);
      by.get(key).push(b);
    }
    return Array.from(by.entries()).map(([key, items]) => {
      const items_sorted = items.slice().sort((a, b) => (a.round || "").localeCompare(b.round || ""));
      const head = items_sorted[0];
      return {
        key,
        upload_id: head.upload_id || null,
        head,
        batches: items_sorted,
        total_rows: items_sorted.reduce((s, b) => s + (b.row_count || 0), 0),
        total_errors: items_sorted.reduce((s, b) => s + (b.error_count || 0), 0),
        latest_at: items_sorted
          .map((b) => b.created_at)
          .sort()
          .reverse()[0],
      };
    }).sort((a, b) => (b.latest_at || "").localeCompare(a.latest_at || ""));
  }, [batches]);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  const act = async (id, kind) => {
    try {
      if (kind === "delete") {
        if (!window.confirm("Roll back and delete this batch and its published records?")) return;
        await api.delete(`/admin/uploads/${id}`);
        toast.success("Batch rolled back");
      } else if (kind === "publish") {
        const { data } = await api.post(`/admin/uploads/${id}/publish`);
        toast.success(`Published ${data.records} records`);
      } else {
        const { data } = await api.post(`/admin/uploads/${id}/revalidate`);
        toast[data.status === "validated" ? "success" : "error"](`Now ${data.status}`);
      }
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const downloadOriginal = async (batchId, filename) => {
    try {
      const { data } = await api.get(`/admin/uploads/${batchId}/original`, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "original.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-batches-page">
      <PageHead
        title="Batches & audit trail"
        subtitle="Each upload file keeps its original bytes and per-round breakdown. Expand a row to publish or roll back individual rounds."
      />

      {loading ? (
        <p className="border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">Loading…</p>
      ) : groups.length === 0 ? (
        <p className="border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          No batches uploaded yet.
        </p>
      ) : (
        <div className="overflow-auto border border-slate-200 bg-white">
          <table className="data-table" data-testid="batches-table">
            <thead>
              <tr>
                <th className="w-6" />
                <th>File</th>
                <th>Context</th>
                <th>Type</th>
                <th>Rounds</th>
                <th className="text-right">Rows</th>
                <th className="text-right">Errors</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <GroupRows
                  key={g.key}
                  g={g}
                  expanded={!!expanded[g.key]}
                  onToggle={() => toggle(g.key)}
                  onAct={act}
                  onDownloadOriginal={downloadOriginal}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <h2 className="font-display text-lg font-bold text-slate-900">Audit trail</h2>
        <div className="mt-2 max-h-72 overflow-auto border border-slate-200 bg-white">
          <table className="data-table" data-testid="full-audit-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {log.map((l) => (
                <tr key={l.id}>
                  <td className="font-mono-num">{new Date(l.at).toLocaleString("en-IN")}</td>
                  <td className="font-semibold uppercase">{l.action}</td>
                  <td>{l.actor || "—"}</td>
                  <td>{l.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
